// src/pages/chat/ChatPage.tsx
// Modern Slack-like chat interface
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Hash, Lock, Send, Plus, Search, Download, Paperclip,
  MessageSquare, Archive, X, FileText, Loader2, ChevronDown, FolderOpen, Menu
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useChatNotification } from '../../contexts/ChatNotificationContext';
import { useProject } from '../../contexts/ProjectContext';
import { chatService } from '../../services/chat.service';
import { ChatRoom, ChatMessage } from '../../types/chat.types';
import { Project } from '../../types/project.types';
import { toast } from 'react-hot-toast';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../config/firebase';

// Avatar colors for users
const AVATAR_COLORS = [
  'from-violet-500 to-purple-600',
  'from-blue-500 to-cyan-600',
  'from-emerald-500 to-teal-600',
  'from-orange-500 to-amber-600',
  'from-pink-500 to-rose-600',
  'from-indigo-500 to-blue-600',
  'from-green-500 to-emerald-600',
  'from-red-500 to-pink-600',
];

// Get consistent color for a user based on their ID
const getAvatarColor = (userId: string) => {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const ChatPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { setUnreadCount } = useChatNotification();
  const { projects, selectedProjectId, selectedProject } = useProject();
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [archivedRooms, setArchivedRooms] = useState<ChatRoom[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newChannelProjectId, setNewChannelProjectId] = useState<string>(''); // For create channel modal
  const [showArchived, setShowArchived] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [initializing, setInitializing] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [uploading, setUploading] = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const projectDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastMessageTimestampRef = useRef<Record<string, number>>({});
  const hasInitializedRef = useRef(false);

  const isProviderAdmin = userProfile?.role === 'provider_admin';
  const isAdmin = userProfile?.role === 'provider_admin' || userProfile?.role === 'client_admin';

  // Navigate to a specific chat room (used by notifications)
  const navigateToRoom = useCallback((room: ChatRoom) => {
    setSelectedRoom(room);
  }, []);

  // Initialize system chats on component mount - optimized for speed
  useEffect(() => {
    const initializeChats = async () => {
      if (!currentUser || !userProfile || hasInitializedRef.current) return;
      hasInitializedRef.current = true;

      try {
        // Initialize system chats
        await chatService.initializeSystemChats();

        // Initialize project chats in parallel using projects from context
        if (projects.length > 0) {
          await Promise.all(
            projects.map(async (project) => {
              await chatService.initializeProjectChats(project.id, project.name);
            })
          );
        }
      } catch (error) {
        console.error('Error initializing chats:', error);
      } finally {
        setInitializing(false);
      }
    };

    initializeChats();
  }, [currentUser, userProfile, projects]);

  // Subscribe to chat rooms with notification support
  useEffect(() => {
    if (!currentUser || !userProfile || initializing) return;

    const unsubscribe = chatService.subscribeToUserRooms(
      currentUser.uid,
      userProfile.role,
      userProfile.projects || [],
      (updatedRooms) => {
        const newUnreadCounts: Record<string, number> = {};

        updatedRooms.forEach(room => {
          if (room.lastMessage && room.lastMessageAt) {
            const messageTime = room.lastMessageAt.toDate().getTime();
            const lastSeen = lastMessageTimestampRef.current[room.id] || 0;

            // Check if this is a new message (not from current user, not in selected room)
            if (messageTime > lastSeen && room.lastMessageSenderId !== currentUser.uid) {
              newUnreadCounts[room.id] = 1;

              // Show notification if room is not currently selected and message is recent (within 10 seconds)
              if (messageTime > Date.now() - 10000) {
                // Store room reference for the toast
                const roomForToast = room;
                toast.custom((t) => (
                  <div
                    className={`${t.visible ? 'animate-enter' : 'animate-leave'}
                      max-w-md w-full bg-white shadow-lg rounded-xl pointer-events-auto flex ring-1 ring-black ring-opacity-5 cursor-pointer`}
                    onClick={() => {
                      navigateToRoom(roomForToast);
                      toast.dismiss(t.id);
                    }}
                  >
                    <div className="flex-1 w-0 p-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 pt-0.5">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                            {room.lastMessageSenderName?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                          </div>
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {room.lastMessageSenderName || 'Unknown'} <span className="text-gray-500 font-normal">in #{room.name}</span>
                          </p>
                          <p className="mt-1 text-sm text-gray-500 truncate">
                            {room.lastMessage}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex border-l border-gray-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toast.dismiss(t.id);
                        }}
                        className="w-full border border-transparent rounded-none rounded-r-xl p-4 flex items-center justify-center text-sm font-medium text-violet-600 hover:text-violet-500 focus:outline-none"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ), { duration: 5000 });
              }
            }

            // Update last seen timestamp
            lastMessageTimestampRef.current[room.id] = messageTime;
          }
        });

        setRooms(updatedRooms);
        setUnreadCounts(newUnreadCounts);

        // Update global notification badge
        const totalUnread = Object.values(newUnreadCounts).reduce((sum, count) => sum + count, 0);
        setUnreadCount(totalUnread);
      }
    );

    return () => unsubscribe();
  }, [currentUser, userProfile, initializing, navigateToRoom, setUnreadCount]);

  // Subscribe to archived rooms
  useEffect(() => {
    if (!isProviderAdmin || !showArchived) return;

    const unsubscribe = chatService.subscribeToArchivedRooms('', (archived) => {
      setArchivedRooms(archived);
    });

    return () => unsubscribe();
  }, [isProviderAdmin, showArchived]);

  // Subscribe to messages
  useEffect(() => {
    if (!selectedRoom) return;

    const unsubscribe = chatService.subscribeToMessages(selectedRoom.id, (updatedMessages) => {
      setMessages(updatedMessages);

      if (selectedRoom?.id) {
        setUnreadCounts(prev => ({ ...prev, [selectedRoom.id]: 0 }));
      }

      updatedMessages.forEach(msg => {
        if (msg.senderId !== currentUser?.uid && !msg.readBy.includes(currentUser?.uid || '')) {
          chatService.markMessageAsRead(msg.id, currentUser?.uid || '');
        }
      });

      scrollToBottom();
    });

    return () => unsubscribe();
  }, [selectedRoom, currentUser]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Toggle dropdown and calculate position for portal
  const toggleProjectDropdown = () => {
    if (!projectDropdownOpen && dropdownButtonRef.current) {
      const rect = dropdownButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
    setProjectDropdownOpen(!projectDropdownOpen);
  };

  // Close project dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        projectDropdownRef.current &&
        !projectDropdownRef.current.contains(event.target as Node) &&
        dropdownButtonRef.current &&
        !dropdownButtonRef.current.contains(event.target as Node)
      ) {
        setProjectDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreateRoom = async () => {
    if (!newRoomName.trim() || !currentUser || !userProfile) return;

    // Use modal selection if set, otherwise use global context selection
    const projectIdForChannel = newChannelProjectId || selectedProjectId || undefined;

    try {
      const roomId = await chatService.createRoom({
        name: newRoomName,
        projectId: projectIdForChannel,
        chatType: 'custom',
        participants: [currentUser.uid],
        participantNames: { [currentUser.uid]: userProfile.name },
        createdBy: currentUser.uid
      });

      setNewRoomName('');
      setNewChannelProjectId(''); // Reset modal selection
      setIsCreatingRoom(false);
      toast.success('Channel created');

      const newRoom = await chatService.getRoom(roomId);
      if (newRoom) {
        setSelectedRoom(newRoom);
      }
    } catch (error) {
      console.error('Error creating room:', error);
      toast.error('Failed to create channel');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedRoom || !currentUser || !userProfile) return;

    try {
      await chatService.sendMessage({
        roomId: selectedRoom.id,
        senderId: currentUser.uid,
        senderName: userProfile.name,
        content: newMessage.trim()
      });

      setNewMessage('');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedRoom || !currentUser || !userProfile) return;

    setUploading(true);
    try {
      const storageRef = ref(storage, `chat-attachments/${selectedRoom.id}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      const messageContent = file.type.startsWith('image/')
        ? `[Image: ${file.name}]\n${downloadURL}`
        : `[File: ${file.name}]\n${downloadURL}`;

      await chatService.sendMessage({
        roomId: selectedRoom.id,
        senderId: currentUser.uid,
        senderName: userProfile.name,
        content: messageContent
      });

      toast.success('File shared');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const exportChatToText = () => {
    if (!selectedRoom || messages.length === 0) return;

    let content = `# ${selectedRoom.name}\n`;
    content += `Exported: ${format(new Date(), 'PPpp')}\n\n`;

    messages.forEach(msg => {
      const timestamp = msg.timestamp ? format(msg.timestamp.toDate(), 'PPpp') : '';
      content += `[${timestamp}] ${msg.senderName}: ${msg.content}\n`;
    });

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedRoom.name.replace(/\s+/g, '-')}_${format(new Date(), 'yyyy-MM-dd')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Chat exported');
  };

  const handleToggleArchive = async (room: ChatRoom) => {
    if (!isProviderAdmin || !currentUser) return;

    try {
      if (room.isArchived) {
        await chatService.unarchiveRoom(room.id);
        toast.success('Channel restored');
      } else {
        await chatService.archiveRoom(room.id, currentUser.uid);
        toast.success('Channel archived');
        if (selectedRoom?.id === room.id) {
          setSelectedRoom(null);
        }
      }
    } catch (error) {
      console.error('Error toggling archive:', error);
      toast.error('Failed to update channel');
    }
  };

  // Organize rooms by category
  const organizeRooms = () => {
    const organized: {
      global: ChatRoom[],
      projects: Record<string, ChatRoom[]>,
      custom: ChatRoom[]
    } = {
      global: [],
      projects: {},
      custom: []
    };

    const roomsToOrganize = showArchived ? archivedRooms : rooms;

    roomsToOrganize.forEach(room => {
      if (room.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        if (room.chatType === 'global-provider' || room.chatType === 'global-staff') {
          organized.global.push(room);
        } else if (room.chatType === 'project-admin' || room.chatType === 'project-all') {
          const projectId = room.projectId || 'unknown';
          if (!organized.projects[projectId]) {
            organized.projects[projectId] = [];
          }
          organized.projects[projectId].push(room);
        } else {
          organized.custom.push(room);
        }
      }
    });

    return organized;
  };

  const organizedRooms = organizeRooms();

  // Group messages by date
  const groupMessagesByDate = (msgs: ChatMessage[]) => {
    const groups: { date: Date; messages: ChatMessage[] }[] = [];
    let currentGroup: { date: Date; messages: ChatMessage[] } | null = null;

    msgs.forEach(msg => {
      const msgDate = msg.timestamp?.toDate() || new Date();

      if (!currentGroup || !isSameDay(currentGroup.date, msgDate)) {
        currentGroup = { date: msgDate, messages: [] };
        groups.push(currentGroup);
      }
      currentGroup.messages.push(msg);
    });

    return groups;
  };

  const formatDateDivider = (date: Date) => {
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'EEEE, MMMM d');
  };

  if (initializing) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-50 rounded-xl">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-violet-600 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Loading messages...</p>
        </div>
      </div>
    );
  }

  const totalUnread = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);

  // Get all rooms as a flat filtered list for search
  const allFilteredRooms = [...organizedRooms.global, ...organizedRooms.custom, ...Object.values(organizedRooms.projects).flat()].filter(
    room => room.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-lg">
      {/* Top Navigation Bar */}
      <div className="bg-slate-50 border-b border-slate-200 px-3 md:px-4 py-2 md:py-3 flex-shrink-0 relative z-20 rounded-t-xl">
        {/* Mobile Header */}
        <div className="flex md:hidden items-center justify-between">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 flex-1 mx-2 min-w-0">
            {selectedRoom ? (
              <>
                <ChannelIcon room={selectedRoom} className="h-4 w-4 text-slate-500 flex-shrink-0" />
                <span className="font-medium text-slate-800 truncate">{selectedRoom.name}</span>
              </>
            ) : (
              <span className="text-slate-500">Select a channel</span>
            )}
          </div>
          {totalUnread > 0 && (
            <span className="bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center font-medium">
              {totalUnread}
            </span>
          )}
        </div>

        {/* Desktop Header */}
        <div className="hidden md:flex items-center gap-4 flex-wrap">
          {/* Search */}
          <div className="relative w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white text-slate-700 text-sm pl-9 pr-3 py-2 rounded-lg border border-slate-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 placeholder-slate-400 outline-none transition-all"
            />
          </div>

          {/* Divider */}
          <div className="h-8 w-px bg-slate-200" />

          {/* System Channels */}
          {organizedRooms.global.length > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-2">System</span>
              {organizedRooms.global.map(room => (
                <button
                  key={room.id}
                  onClick={() => setSelectedRoom(room)}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                    ${selectedRoom?.id === room.id
                      ? 'bg-violet-600 text-white shadow-sm'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                    }
                  `}
                >
                  <ChannelIcon room={room} className="h-3.5 w-3.5" />
                  <span className="truncate max-w-[100px]">{formatChannelNameShort(room.name)}</span>
                  {unreadCounts[room.id] > 0 && selectedRoom?.id !== room.id && (
                    <span className="bg-violet-600 text-white text-xs rounded-full min-w-[18px] h-4.5 px-1.5 flex items-center justify-center font-medium">
                      {unreadCounts[room.id]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Project Channels - Dropdown for providers, inline for others */}
          {Object.keys(organizedRooms.projects).length > 0 && (
            <>
              <div className="h-8 w-px bg-slate-200" />

              {/* Provider users see dropdown */}
              {(userProfile?.role === 'provider_admin' || userProfile?.role === 'provider_user') ? (
                <>
                  <button
                    ref={dropdownButtonRef}
                    onClick={toggleProjectDropdown}
                    className={`
                      flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                      ${projectDropdownOpen
                        ? 'bg-violet-100 text-violet-700 border border-violet-300'
                        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                      }
                    `}
                  >
                    <FolderOpen className="h-4 w-4" />
                    <span>Projects</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${projectDropdownOpen ? 'rotate-180' : ''}`} />
                    {/* Total unread badge for projects */}
                    {(() => {
                      const totalProjectUnread = Object.entries(organizedRooms.projects)
                        .flatMap(([_, rooms]) => rooms)
                        .reduce((sum, room) => sum + (unreadCounts[room.id] || 0), 0);
                      return totalProjectUnread > 0 ? (
                        <span className="bg-violet-600 text-white text-xs rounded-full min-w-[18px] h-4.5 px-1.5 flex items-center justify-center font-medium">
                          {totalProjectUnread}
                        </span>
                      ) : null;
                    })()}
                  </button>

                  {/* Dropdown Menu - Rendered via Portal */}
                  {projectDropdownOpen && createPortal(
                    <div
                      ref={projectDropdownRef}
                      className="fixed w-72 bg-white rounded-xl shadow-lg border border-slate-200 py-2 max-h-96 overflow-y-auto"
                      style={{ top: dropdownPosition.top, left: dropdownPosition.left, zIndex: 9999 }}
                    >
                      {Object.entries(organizedRooms.projects).map(([projectId, projectRooms], index) => {
                        const project = projects.find(p => p.id === projectId);
                        const color = PROJECT_COLORS[index % PROJECT_COLORS.length];

                        return (
                          <div key={projectId} className="mb-1 last:mb-0">
                            {/* Project Header */}
                            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50">
                              <span className={`w-6 h-6 ${color.bg} rounded text-white text-xs font-bold flex items-center justify-center`}>
                                {getProjectInitials(project?.name || 'P')}
                              </span>
                              <span className="text-sm font-semibold text-slate-700 truncate">
                                {project?.name || 'Project'}
                              </span>
                            </div>
                            {/* Project Channels */}
                            <div className="py-1">
                              {projectRooms.map(room => (
                                <button
                                  key={room.id}
                                  onClick={() => {
                                    setSelectedRoom(room);
                                    setProjectDropdownOpen(false);
                                  }}
                                  className={`
                                    w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors
                                    ${selectedRoom?.id === room.id
                                      ? 'bg-violet-100 text-violet-700'
                                      : 'text-slate-600 hover:bg-slate-100'
                                    }
                                  `}
                                >
                                  <ChannelIcon room={room} className="h-4 w-4 flex-shrink-0" />
                                  <span className="flex-1 text-left truncate">{formatChannelNameShort(room.name)}</span>
                                  {unreadCounts[room.id] > 0 && (
                                    <span className="bg-violet-600 text-white text-xs rounded-full min-w-[18px] h-5 px-1.5 flex items-center justify-center font-medium">
                                      {unreadCounts[room.id]}
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>,
                    document.body
                  )}
                </>
              ) : (
                /* Client/field users see inline project channels */
                <div className="flex items-center gap-2 flex-wrap">
                  {Object.entries(organizedRooms.projects).map(([projectId, projectRooms], index) => {
                    const project = projects.find(p => p.id === projectId);
                    const color = PROJECT_COLORS[index % PROJECT_COLORS.length];

                    return (
                      <div key={projectId} className="flex items-center gap-1">
                        <span className={`w-5 h-5 ${color.bg} rounded text-white text-[10px] font-bold flex items-center justify-center mr-1`}>
                          {getProjectInitials(project?.name || 'P')}
                        </span>
                        {projectRooms.map(room => (
                          <button
                            key={room.id}
                            onClick={() => setSelectedRoom(room)}
                            className={`
                              flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                              ${selectedRoom?.id === room.id
                                ? 'bg-violet-600 text-white shadow-sm'
                                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                              }
                            `}
                          >
                            <ChannelIcon room={room} className="h-3.5 w-3.5" />
                            <span className="truncate max-w-[100px]">{formatChannelNameShort(room.name)}</span>
                            {unreadCounts[room.id] > 0 && selectedRoom?.id !== room.id && (
                              <span className="bg-violet-600 text-white text-xs rounded-full min-w-[18px] h-4.5 px-1.5 flex items-center justify-center font-medium">
                                {unreadCounts[room.id]}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Custom/Direct Messages */}
          {organizedRooms.custom.length > 0 && (
            <>
              <div className="h-8 w-px bg-slate-200" />
              <div className="flex items-center gap-1">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-2">DMs</span>
                {organizedRooms.custom.map(room => (
                  <button
                    key={room.id}
                    onClick={() => setSelectedRoom(room)}
                    className={`
                      flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                      ${selectedRoom?.id === room.id
                        ? 'bg-violet-600 text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                      }
                    `}
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    <span className="truncate max-w-[80px]">{room.name}</span>
                    {unreadCounts[room.id] > 0 && selectedRoom?.id !== room.id && (
                      <span className="bg-violet-600 text-white text-xs rounded-full min-w-[18px] h-4.5 px-1.5 flex items-center justify-center font-medium">
                        {unreadCounts[room.id]}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Actions */}
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={() => setIsCreatingRoom(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">New Channel</span>
              </button>
            )}
            {isProviderAdmin && (
              <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="rounded border-slate-300 text-violet-500 focus:ring-violet-500 h-3.5 w-3.5"
                />
                <span className="hidden sm:inline">Archived</span>
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Channel Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-xl overflow-y-auto">
            {/* Mobile Menu Header */}
            <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800">Channels</h2>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search channels..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-50 text-slate-700 text-sm pl-9 pr-3 py-2 rounded-lg border border-slate-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 placeholder-slate-400 outline-none"
                />
              </div>
            </div>

            {/* System Channels */}
            {organizedRooms.global.length > 0 && (
              <div className="px-4 py-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">System</h3>
                <div className="space-y-1">
                  {organizedRooms.global.map(room => (
                    <button
                      key={room.id}
                      onClick={() => {
                        setSelectedRoom(room);
                        setMobileMenuOpen(false);
                      }}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                        ${selectedRoom?.id === room.id
                          ? 'bg-violet-100 text-violet-700'
                          : 'text-slate-600 hover:bg-slate-100'
                        }
                      `}
                    >
                      <ChannelIcon room={room} className="h-4 w-4 flex-shrink-0" />
                      <span className="flex-1 text-left truncate">{formatChannelNameShort(room.name)}</span>
                      {unreadCounts[room.id] > 0 && (
                        <span className="bg-violet-600 text-white text-xs rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center font-medium">
                          {unreadCounts[room.id]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Project Channels */}
            {Object.entries(organizedRooms.projects).map(([projectId, projectRooms], index) => {
              const project = projects.find(p => p.id === projectId);
              const color = PROJECT_COLORS[index % PROJECT_COLORS.length];

              return (
                <div key={projectId} className="px-4 py-3 border-t border-slate-100">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-5 h-5 ${color.bg} rounded text-white text-[10px] font-bold flex items-center justify-center`}>
                      {getProjectInitials(project?.name || 'P')}
                    </span>
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">
                      {project?.name || 'Project'}
                    </h3>
                  </div>
                  <div className="space-y-1">
                    {projectRooms.map(room => (
                      <button
                        key={room.id}
                        onClick={() => {
                          setSelectedRoom(room);
                          setMobileMenuOpen(false);
                        }}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                          ${selectedRoom?.id === room.id
                            ? 'bg-violet-100 text-violet-700'
                            : 'text-slate-600 hover:bg-slate-100'
                          }
                        `}
                      >
                        <ChannelIcon room={room} className="h-4 w-4 flex-shrink-0" />
                        <span className="flex-1 text-left truncate">{formatChannelNameShort(room.name)}</span>
                        {unreadCounts[room.id] > 0 && (
                          <span className="bg-violet-600 text-white text-xs rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center font-medium">
                            {unreadCounts[room.id]}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Direct Messages */}
            {organizedRooms.custom.length > 0 && (
              <div className="px-4 py-3 border-t border-slate-100">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Direct Messages</h3>
                <div className="space-y-1">
                  {organizedRooms.custom.map(room => (
                    <button
                      key={room.id}
                      onClick={() => {
                        setSelectedRoom(room);
                        setMobileMenuOpen(false);
                      }}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                        ${selectedRoom?.id === room.id
                          ? 'bg-violet-100 text-violet-700'
                          : 'text-slate-600 hover:bg-slate-100'
                        }
                      `}
                    >
                      <MessageSquare className="h-4 w-4 flex-shrink-0" />
                      <span className="flex-1 text-left truncate">{room.name}</span>
                      {unreadCounts[room.id] > 0 && (
                        <span className="bg-violet-600 text-white text-xs rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center font-medium">
                          {unreadCounts[room.id]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* New Channel Button */}
            {isAdmin && (
              <div className="px-4 py-3 border-t border-slate-100">
                <button
                  onClick={() => {
                    setIsCreatingRoom(true);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  New Channel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Chat Area - Full Height */}
      {selectedRoom ? (
        <div className="flex-1 flex flex-col bg-white min-w-0 min-h-0 overflow-hidden rounded-b-xl">
          {/* Chat Header - Compact */}
          <div className="h-12 px-4 flex items-center justify-between border-b border-slate-200 flex-shrink-0 bg-white">
            <div className="flex items-center gap-2 min-w-0">
              <ChannelIcon room={selectedRoom} className="h-4 w-4 text-slate-500 flex-shrink-0" />
              <h2 className="font-semibold text-slate-900 truncate">{selectedRoom.name}</h2>
              <span className="text-xs text-slate-400 hidden sm:inline">•</span>
              <p className="text-xs text-slate-500 truncate hidden sm:block">
                {getChannelDescription(selectedRoom)}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={exportChatToText}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                title="Export chat"
              >
                <Download className="h-4 w-4" />
              </button>
              {isProviderAdmin && selectedRoom.chatType === 'custom' && (
                <button
                  onClick={() => handleToggleArchive(selectedRoom)}
                  className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  title={selectedRoom.isArchived ? 'Unarchive' : 'Archive'}
                >
                  <Archive className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {messages.length === 0 ? (
              <EmptyChannelState room={selectedRoom} />
            ) : (
              <div className="py-4">
                {groupMessagesByDate(messages).map((group, groupIndex) => (
                  <div key={groupIndex}>
                    {/* Date Divider */}
                    <div className="flex items-center gap-3 px-4 py-4">
                      <div className="flex-1 h-px bg-slate-200" />
                      <span className="text-xs font-medium text-slate-500 bg-white px-2">
                        {formatDateDivider(group.date)}
                      </span>
                      <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    {/* Messages for this date */}
                    {group.messages.map((message, msgIndex) => {
                      const prevMessage = msgIndex > 0 ? group.messages[msgIndex - 1] : null;
                      const isConsecutive = prevMessage &&
                        prevMessage.senderId === message.senderId &&
                        message.timestamp && prevMessage.timestamp &&
                        (message.timestamp.toDate().getTime() - prevMessage.timestamp.toDate().getTime()) < 5 * 60 * 1000;

                      return (
                        <MessageItem
                          key={message.id}
                          message={message}
                          isOwn={message.senderId === currentUser?.uid}
                          isConsecutive={isConsecutive}
                        />
                      );
                    })}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Message Input */}
          <div className="px-4 pb-4 flex-shrink-0">
            <div className="border border-slate-300 rounded-xl bg-white shadow-sm focus-within:border-violet-500 focus-within:ring-1 focus-within:ring-violet-500 transition-all">
              <div className="flex items-end gap-2 p-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.txt"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50 transition-colors"
                  title="Attach file"
                >
                  <Paperclip className="h-5 w-5" />
                </button>
                <textarea
                  ref={inputRef}
                  placeholder={`Message #${selectedRoom.name.toLowerCase().replace(/\s+/g, '-')}`}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  className="flex-1 resize-none border-0 bg-transparent text-slate-900 placeholder-slate-400 focus:ring-0 text-sm py-2 max-h-32"
                  disabled={uploading}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || uploading}
                  className="p-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* No Room Selected */
        <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 overflow-hidden rounded-b-xl relative z-10">
          <div className="w-16 h-16 bg-slate-200 rounded-2xl flex items-center justify-center mb-4">
            <MessageSquare className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-1">Welcome to Team Chat</h3>
          <p className="text-slate-500 text-sm text-center max-w-xs">
            Select a channel from the menu above to start messaging your team
          </p>
          {totalUnread > 0 && (
            <div className="mt-4 px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">
              {totalUnread} unread message{totalUnread > 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {/* Create Channel Modal */}
      {isCreatingRoom && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Create a channel</h3>
                <button
                  onClick={() => {
                    setIsCreatingRoom(false);
                    setNewRoomName('');
                  }}
                  className="p-1 text-slate-400 hover:text-slate-600 rounded"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Channels are where your team communicates
              </p>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Project (optional)
                </label>
                <select
                  value={newChannelProjectId || selectedProjectId || ''}
                  onChange={(e) => setNewChannelProjectId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                >
                  <option value="">General (no project)</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Channel name
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="e.g. marketing-team"
                    value={newRoomName}
                    onChange={(e) => setNewRoomName(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    autoFocus
                  />
                </div>
              </div>
            </div>
            <div className="p-4 bg-slate-50 flex gap-3">
              <button
                onClick={() => {
                  setIsCreatingRoom(false);
                  setNewRoomName('');
                }}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRoom}
                disabled={!newRoomName.trim()}
                className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Create Channel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Project color palette for badges
const PROJECT_COLORS = [
  { bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-100' },
  { bg: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-100' },
  { bg: 'bg-orange-500', text: 'text-orange-600', light: 'bg-orange-100' },
  { bg: 'bg-pink-500', text: 'text-pink-600', light: 'bg-pink-100' },
  { bg: 'bg-cyan-500', text: 'text-cyan-600', light: 'bg-cyan-100' },
  { bg: 'bg-purple-500', text: 'text-purple-600', light: 'bg-purple-100' },
  { bg: 'bg-amber-500', text: 'text-amber-600', light: 'bg-amber-100' },
  { bg: 'bg-red-500', text: 'text-red-600', light: 'bg-red-100' },
];

// Channel Icon Component
const ChannelIcon: React.FC<{ room: ChatRoom; className?: string }> = ({ room, className }) => {
  if (room.chatType === 'global-provider' || room.chatType === 'project-admin') {
    return <Lock className={className} />;
  }
  return <Hash className={className} />;
};

// Message Item Component
const MessageItem: React.FC<{
  message: ChatMessage;
  isOwn: boolean;
  isConsecutive?: boolean;
}> = ({ message, isOwn, isConsecutive }) => {
  const isFileMessage = message.content.includes('[File:') || message.content.includes('[Image:');
  const fileMatch = message.content.match(/\[(File|Image): ([^\]]+)\]\n(.+)/);

  const initials = message.senderName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  if (isConsecutive) {
    return (
      <div className="px-4 py-0.5 hover:bg-slate-50 group">
        <div className="pl-12">
          <span className="text-xs text-slate-400 opacity-0 group-hover:opacity-100 absolute -ml-8">
            {message.timestamp && format(message.timestamp.toDate(), 'h:mm a')}
          </span>
          {isFileMessage && fileMatch ? (
            <FileContent fileMatch={fileMatch} />
          ) : (
            <p className="text-sm text-slate-800">{message.content}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-2 hover:bg-slate-50 group">
      <div className="flex gap-3">
        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${getAvatarColor(message.senderId)} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-semibold text-sm text-slate-900">{message.senderName}</span>
            <span className="text-xs text-slate-400">
              {message.timestamp && format(message.timestamp.toDate(), 'h:mm a')}
            </span>
          </div>
          {isFileMessage && fileMatch ? (
            <FileContent fileMatch={fileMatch} />
          ) : (
            <p className="text-sm text-slate-800 mt-0.5">{message.content}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// File Content Component
const FileContent: React.FC<{ fileMatch: RegExpMatchArray }> = ({ fileMatch }) => {
  const isImage = fileMatch[1] === 'Image';
  const fileName = fileMatch[2];
  const url = fileMatch[3];

  if (isImage) {
    return (
      <div className="mt-1">
        <img
          src={url}
          alt={fileName}
          className="max-w-xs rounded-lg cursor-pointer hover:opacity-90 shadow-sm"
          onClick={() => window.open(url, '_blank')}
        />
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-1 inline-flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
    >
      <FileText className="h-4 w-4 text-slate-500" />
      <span className="text-sm text-slate-700">{fileName}</span>
      <Download className="h-4 w-4 text-slate-400" />
    </a>
  );
};

// Empty Channel State
const EmptyChannelState: React.FC<{ room: ChatRoom }> = ({ room }) => (
  <div className="flex flex-col items-center justify-center h-full px-4">
    <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mb-4">
      <ChannelIcon room={room} className="h-8 w-8 text-violet-600" />
    </div>
    <h3 className="text-xl font-semibold text-slate-900 mb-1">
      Welcome to #{room.name.toLowerCase().replace(/\s+/g, '-')}
    </h3>
    <p className="text-slate-500 text-sm text-center max-w-sm">
      This is the start of the #{room.name.toLowerCase().replace(/\s+/g, '-')} channel.
      Send a message to start the conversation.
    </p>
  </div>
);

// Helper functions
function getChannelDescription(room: ChatRoom): string {
  switch (room.chatType) {
    case 'global-provider':
      return 'Provider admins and users';
    case 'global-staff':
      return 'All team members';
    case 'project-admin':
      return 'Project administrators';
    case 'project-all':
      return 'All project members';
    default:
      return `${Object.keys(room.participantNames || {}).length} members`;
  }
}

// Format channel name for compact display in top bar
function formatChannelNameShort(name: string): string {
  return name
    .replace('Provider Admin Chat', 'Admin')
    .replace('Provider Team Chat', 'Team')
    .replace(' Admin Chat', ' Admin')
    .replace(' Team Chat', ' Team');
}

// Get initials for project badge
function getProjectInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();
}

export default ChatPage;
